import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  selectCurrentStudioScene,
  saveScene,
  StudioImage,
  StudioImageShapeDescriptor,
  StudioVideoShapeDescriptor,
  StudioScene,
  VideoInstance,
  selectStudioVideos,
  selectStudioVideoInstances,
  StudioVideo,
  selectStudioImages,
  StudioShapeDescriptor,
  LayoutMetaData,
  StudioProject,
  selectCurrentProject,
  StudioService,
} from '..';
import {
  fullWidth,
  leftHalf,
  rightHalf,
  rightBottomQuarter,
  rightTopQuarter,
  leftTopQuarter,
  leftBottomQuarter,
  centerTopThird,
  centerBottomThird,
  isLeftHalf,
  isRightHalf,
  leftTopThird,
  leftBottomThird,
  rightTopThird,
  rightBottomThird,
  centerTopHalf,
  centerBottomHalf,
  center09,
  center07,
  center06,
  center05,
  center075,
  letfTop,
  rightBottom,
  rightTop,
  leftBottom,
  _leftHalf,
  _rightHalf,
  center04,
  center03,
} from './layout.constants';
import { StudioCell } from '../studio.types';
import { cloneDeep, isEqual, omit } from 'lodash';
import * as uuidv1 from 'uuid/v1';
import {
  StudioShapeBackground,
  StudioTextShape,
  StudioTextShapeDescriptor,
} from '../text/studio-text-shape';
import { CanvasService } from '../../../../services/canvas/canvas.service';
import {
  currentOrganisation,
  selectCurrentOrganizationID,
} from '../../../../organisation/organisation.selector';
import { Organisation } from '../../../../organisation/organisation.interface';
import { LayoutCellDefinition, RootLayoutCell } from './layout-cell';

// export interface

export enum TextBreak {
  None = 'none',
  Word = 'word',
  Random = 'random',
  Manual = 'manual',
}

export enum TextStyle {
  Default = 'default',
  Capitalized = 'capitalized',
  StrokeShadow = 'stroked-shadow',
  ShiftedWords = 'shifted-words',
}

export enum Capitalization {
  None = 'none',
  All = 'all',
  First = 'first',
}

export type Shape =
  | StudioTextShapeDescriptor
  | StudioImageShapeDescriptor
  | StudioVideoShapeDescriptor;

@Injectable({ providedIn: 'root' })
export class StudioLayoutService {
  currentScene: StudioScene;
  videoInstances: Record<string, VideoInstance>;
  videos: Record<string, StudioVideo>;
  images: Record<string, StudioImage>;

  currentOrganisation: Organisation;
  currentProject: StudioProject;

  get currentFontFamily() {
    return (
      this.currentProject?.fontFamily ||
      this.currentOrganisation?.fontFamily ||
      'Goldman'
    );
  }
  constructor(
    private readonly store: Store,
    private readonly cs: CanvasService,

    private readonly studioService: StudioService,
  ) {
    this.store.select(currentOrganisation).subscribe(org => {
      this.currentOrganisation = org;
    });

    this.store.select(selectCurrentProject).subscribe(project => {
      this.currentProject = project;
    });

    this.store.select(selectCurrentStudioScene).subscribe(scene => {
      this.currentScene = cloneDeep(scene);
    });

    this.store.select(selectStudioVideos).subscribe(videos => {
      this.videos = videos;
    });

    this.store.select(selectStudioVideoInstances).subscribe(videoInstances => {
      this.videoInstances = videoInstances;
    });
    this.store.select(selectStudioImages).subscribe(images => {
      this.images = images;
    });
  }

  getTextShape(shape: Shape) {
    return 'text' in shape ? shape : undefined;
  }

  getImageShape(shape: Shape) {
    return 'imageId' in shape ? shape : undefined;
  }
  getVideoShape(shape: Shape) {
    return 'videoInstanceId' in shape ? shape : undefined;
  }

  getMediaShape(shape: Shape) {
    return this.getImageShape(shape) || this.getVideoShape(shape);
  }
  isText(shape: Shape) {
    return 'text' in shape;
  }

  isMedia(shape: Shape) {
    return 'imageId' in shape || 'videoInstanceId' in shape;
  }

  isImage(shape: Shape) {
    return 'imageId' in shape;
  }

  isVideo(shape: Shape) {
    return 'videoInstanceId' in shape;
  }

  isMediaLandscape(shape: Shape) {
    const mediaShape = this.getMediaShape(shape);
    if (!mediaShape) {
      return false;
    }
    if (this.isImage(mediaShape)) {
      return this.isImageLandscape(mediaShape);
    }
    if (this.isVideo(mediaShape)) {
      return this.isVideoLandscape(mediaShape);
    }
    return false;
  }

  singleImageOptions = [
    {
      key: 'full-width',
      cell: fullWidth,
    },
    {
      key: 'center-09',
      cell: center09,
    },
    {
      key: 'center-075',
      cell: center075,
    },
    {
      key: 'center-05',
      cell: center05,
    },
  ];

  optionMap = {
    media: {
      'full-width': fullWidth,
      'center-09': center09,
      'center-075': center075,
      'center-05': center05,
    },
    texts: {
      'center-top-bottom': [centerTopThird, centerBottomThird],
      diagonal: [leftTopThird, rightBottomThird],
      'vertical-side': [rightTopThird, leftBottomThird],
    },
    text: {
      'left-bottom-overflow': leftBottomThird,
      'right-bottom-overflow': rightBottomThird,
      'left-top-overflow': leftTopThird,
      'right-top-overflow': rightTopThird,
    },
  };

  regenerateLayout() {
    const { texts, media } = this.getNewLayout();
    // -- // -- // -- //
    this.currentScene.layout = {
      ...this.currentScene.layout,
      texts,
      media,
    };

    this.currentScene.texts.forEach((textId, index) => {
      this.currentScene.shapes[textId].cell =
        this.optionMap.texts[texts][index];
    });

    this.currentScene.images.forEach(imageId => {
      this.currentScene.shapes[imageId].cell = this.optionMap.media[media];
    });

    this.saveCurrentScene();
  }

  getNewLayout(): LayoutMetaData {
    const layoutOptions = this.getLayoutOptions(
      this.currentScene.layout ||
        ({
          media: 'full-width',
          texts: 'center-top-bottom',
        } as LayoutMetaData),
    );

    // -- //
    const outerlength = layoutOptions.length;
    const innerLength = layoutOptions[0].length;

    const randomIndex = Math.floor(Math.random() * outerlength);
    const randomInnerIndex = Math.floor(Math.random() * innerLength);

    const newLayout = layoutOptions[randomIndex][randomInnerIndex];

    return newLayout as LayoutMetaData;
  }

  getLayoutOptions(currentLayout: LayoutMetaData) {
    // -- //
    console.log('getLayoutOptions', currentLayout);
    const { texts, media, images, videos } = currentLayout;

    const mediaOptions = Object.keys(this.optionMap.media)
      .filter(key => key != media)
      .map(key => ({
        media: key,
      }));

    const textOptions = Object.keys(this.optionMap.texts)
      .filter(key => key != texts)
      .map(key => ({
        texts: key,
      }));

    // -- //
    return mediaOptions.map(textOption => {
      return textOptions.map(mediaOption => ({
        ...textOption,
        ...mediaOption,
      }));
    });
  }

  getNextLayout() {}

  getPreviousLayout() {}

  isVideoLandscape(shape: StudioVideoShapeDescriptor) {
    const { videoInstanceId } = shape;
    const videoInstance = this.videoInstances[videoInstanceId];
    const video = this.videos[videoInstance.mediaId];
    const { width, height } = video;
    return width > height;
  }

  isImageLandscape(shape: StudioImageShapeDescriptor) {
    const { imageId } = shape;
    const image = this.images[imageId];
    const { width, height } = image;
    return width > height;
  }

  getRandomFrom(ids: string[], options: Array<StudioCell[]>) {
    // -- //
    const [id1, id2, id3] = ids;
    const results = [];
    switch (ids.length) {
      case 2:
        options.forEach(option => {
          const [cell1, cell2] = option;
          results.push({
            [id1]: cell1,
            [id2]: cell2,
          });
          // results.push({
          //   [id1]: cell2,
          //   [id2]: cell1,
          // });
        });
        break;
      case 3:
        options.forEach(option => {
          const [c1, c2, c3] = option;
          results.push({
            [id1]: c1,
            [id2]: c2,
            [id3]: c3,
          });
          results.push({
            [id1]: c2,
            [id2]: c1,
            [id3]: c3,
          });
          results.push({
            [id1]: c1,
            [id2]: c3,
            [id3]: c2,
          });
          results.push({
            [id1]: c2,
            [id2]: c3,
            [id3]: c1,
          });
          results.push({
            [id1]: c3,
            [id2]: c1,
            [id3]: c2,
          });
          results.push({
            [id1]: c3,
            [id2]: c2,
            [id3]: c1,
          });
        });

        break;
    }
    return results[Math.floor(Math.random() * results.length)];
  }

  calcUniformFontSizeForTexts(
    descriptors: Array<{ text: string; cell: StudioCell; fontFamily?: string }>,
  ): number {
    const sizes = descriptors.map(({ text, cell, fontFamily }) =>
      StudioTextShape.getFontSizeByCell(
        this.cs,
        cell,
        text,
        fontFamily || 'Arial',
      ),
    );
    return Math.min(...sizes);
  }

  addTextShape(text: string) {
    this.addTo('center', { text, cell: undefined });
    this.saveCurrentScene();
  }
  addNewShape(shape: Shape) {
    const newMedia = this.getMediaShape(shape);
    const newText = this.getTextShape(shape);
    // At this point I need to know the topology

    const { texts, images, videoInstances } = this.currentScene;
    const textLength = texts?.length || 0;
    const imageLength = images?.length || 0;
    const videoLength = videoInstances?.length || 0;
    const mediaLength = imageLength + videoLength;

    // Lets cover the most common case
    // 1. There's a text in the middle -> add media left or right
    // 2. There's a text and a media ->

    // The new element is media
    if (newMedia) {
      if (this.isMediaLandscape(newMedia)) {
        // Above - below
        // Add to bottom
        if (textLength >= 1 && mediaLength == 0) {
          this.addTo('center', shape);
        } else {
          this.addTo('bottom', shape);
        }
      } else {
        // Side by side
        // Add to the right
        this.addTo('right', shape);
      }
    }
    // The new element is text
    if (newText) {
      // Add to the right
      // Set layout to row

      const { texts } = this.currentScene;
      if (texts.length == 1) {
        // -- //
        const [id] = texts;
        this.currentScene.shapes[id].cell.vAlign = 'bottom';
        this.addTo('bottom', shape);
      } else {
        this.addTo('right', shape);
      }
    }
  }

  addTo(target: 'right' | 'left' | 'top' | 'bottom' | 'center', shape: Shape) {
    // -- // -- //

    // -- //
    console.log('addTo', target, shape);
    const newId =
      (shape as StudioImageShapeDescriptor).imageId ||
      (shape as StudioVideoShapeDescriptor).videoInstanceId ||
      uuidv1();
    // -- //
    if (this.isText(shape)) {
      this.currentScene.texts.push(newId);
    }
    let oldRatio: number, newRatio: number;
    let cell: StudioCell;
    switch (target) {
      case 'center':
        cell = fullWidth;
        // -- // -- // -- //
        break;
      case 'right':
        const distinctXs = new Set(
          Object.values(this.currentScene.shapes).map(shape => shape.cell.x),
        );
        // -- // -- // -- //
        oldRatio = 1 / distinctXs.size;
        newRatio = 1 / (distinctXs.size + 1);
        // -- //
        for (const id in this.currentScene.shapes) {
          this.currentScene.shapes[id].cell.w *= newRatio / oldRatio;
          this.currentScene.shapes[id].cell.x *= newRatio / oldRatio;
        }
        cell = {
          w: newRatio,
          h: 1,
          x: 1 - newRatio,
          y: 0,
        };
        break;
      case 'bottom':
        // Element is shifted to the top

        // I need to know how many elements are there vertically.
        const distinctYs = new Set(
          Object.values(this.currentScene.shapes).map(shape => shape.cell.y),
        );

        oldRatio = 1 / distinctYs.size;
        newRatio = 1 / (distinctYs.size + 1);

        for (const id in this.currentScene.shapes) {
          this.currentScene.shapes[id].cell.h *= newRatio / oldRatio;
          this.currentScene.shapes[id].cell.y *= newRatio / oldRatio;
        }
        // -- // -- // -- // -- //
        cell = {
          w: 1,
          h: newRatio,
          x: 0,
          y: 1 - newRatio,
          vAlign: this.isText(shape) ? 'top' : undefined,
          widthRatio: this.isText(shape) ? 0.4 : undefined,
        };
        break;
    }

    this.currentScene.shapes[newId] = {
      ...shape,
      cell,
    };
  }

  getTextBreakOptions(
    textShape: StudioTextShapeDescriptor,
  ): { break: TextBreak; lines: string[][] }[] {
    const { text } = textShape;

    const words = textShape.text.split(/\s+/);
    switch (words.length) {
      case 1:
        // -- // -- //
        // In this case we can move it to center, top | bottom with some overflow
        // We can play around with the size
        // or we just multiply with stroked background
        // here we can add the style dims
        return [
          {
            break: TextBreak.None,
            lines: [words],
          },
        ];
      case 2:
        return [
          {
            break: TextBreak.None,
            lines: [words],
          },
          {
            break: TextBreak.Word,
            lines: words.map(word => [word]),
          },
        ];
      case 3:
        return [
          {
            break: TextBreak.None,
            lines: [words],
          },
          {
            break: TextBreak.Word,
            lines: words.map(word => [word]),
          },
          {
            break: TextBreak.Random,
            lines: [words.slice(0, 2), words.slice(2)],
          },
          {
            break: TextBreak.Random,
            lines: [words.slice(0, 1), words.slice(1)],
          },
        ];
      case 4:
        // TODO check here the ratio of the words
        return [
          {
            break: TextBreak.None,
            lines: [words],
          },
          {
            break: TextBreak.Word,
            lines: words.map(word => [word]),
          },
          {
            break: TextBreak.Random,
            lines: [words.slice(0, 2), words.slice(2)],
          },
        ];
      default:
        return [
          {
            break: TextBreak.None,
            lines: [words],
          },
        ];
    }
  }

  getPreviewScenes() {
    const textLayouts = this.textPossibleLayouts(
      this.currentScene.shapes[
        this.currentScene.texts[0]
      ] as StudioTextShapeDescriptor,
    );
    return textLayouts.map(textLayout => {
      return textLayout.map(textShape => {
        return {
          ...this.currentScene,
          shapes: {
            ...this.currentScene.shapes,
            [this.currentScene.texts[0]]: textShape,
          },
        };
      });
    });
  }

  textPossibleLayouts(
    textShape: StudioTextShapeDescriptor,
  ): StudioTextShapeDescriptor[][] {
    const breakOptions = this.getTextBreakOptions(textShape);
    // -- // -- //
    // -- // -- //
    // const { text } = textShape; // -- //

    if (breakOptions.length == 1 && breakOptions[0].break == TextBreak.None) {
      const [option] = breakOptions;
      return [
        [letfTop('twoThird'), center07, rightBottom('twoThird')].map(cell => ({
          ...textShape,
          lines: option.lines,
          cell,
        })),
        [
          leftBottom('twoThird', true),
          center07,
          rightTop('twoThird', true),
        ].map(cell => ({
          ...textShape,
          lines: option.lines,
          cell,
        })),
      ];
    }

    const cells = ['left', 'center', 'right'];

    return breakOptions.map(option => {
      // -- //
      let cells: string[] = [];
      switch (option.break) {
        case TextBreak.None:
          cells = ['left-top', 'center', 'right-bottom'];
          break;
        default:
          cells = ['left', 'center', 'right'];
          break;
      }

      return cells.map(cellId => {
        let cell: StudioCell;

        switch (cellId) {
          case 'left-top':
            cell = letfTop('twoThird');
            break;
          case 'right-bottom':
            cell = rightBottom('twoThird');
            break;
          case 'center':
            cell = center07;
            break;
          case 'left':
            cell = leftHalf;
            break;
          case 'right':
            cell = rightHalf;
            break;
        }
        const { break: textBreak, lines } = option;
        return {
          ...textShape,
          lines,
          cell,
        };
      });
    });
  }

  getTextLayouts(texts: string[]): {
    cells: Record<string, StudioCell>;
    layout: LayoutMetaData;
  }[][] {
    const textDescriptors = texts.map(id => this.currentScene.shapes[id]);

    switch (textDescriptors.length) {
      // case 1:
      //   return [this.textPossibleLayouts(textDescriptors[0])];
      case 2:
        const [text1, text2] = texts;

        const [fs1, fs2] = [text1, text2]
          .map(id => this.currentScene.shapes[id] as StudioTextShapeDescriptor)
          .map(({ text, fontFamily }) => {
            return StudioTextShape.getFontSizeByCell(
              this.cs,
              leftHalf,
              text,
              fontFamily || this.currentFontFamily,
            );
          });

        const fontSize = Math.min(fs1, fs2);

        const background: StudioShapeBackground = {
          type: 'color',
          color: '#000000',
          opacity: 0.1,
          radius: 32,
          padding: 0.05,
        };
        return [
          [
            {
              cells: {
                [text1]: { ...leftHalf, fontSize },
                [text2]: { ...rightHalf, fontSize },
              },
              layout: {
                texts: 'left-right' as LayoutMetaData['texts'],
              },
            },
            {
              cells: {
                [text1]: _leftHalf(0.05, 'center'),
                [text2]: _rightHalf(0.0, 'center'),
              },
              layout: {
                texts: 'right-left' as LayoutMetaData['texts'],
              },
            },
            {
              cells: {
                [text1]: { ..._leftHalf(0.1, 'center'), fontSize, background },
                [text2]: { ..._rightHalf(0.1, 'center'), fontSize, background },
              },
              layout: {
                texts: 'right-left' as LayoutMetaData['texts'],
              },
            },
          ],
          [
            {
              cells: {
                [text1]: centerTopHalf,
                [text2]: centerBottomHalf,
              },
              layout: {
                texts: 'top-bottom' as LayoutMetaData['texts'],
              },
            },
            {
              cells: {
                [text1]: centerBottomHalf,
                [text2]: centerTopHalf,
              },
              layout: {
                texts: 'bottom-top' as LayoutMetaData['texts'],
              },
            },
            {
              cells: {
                [text1]: centerBottomHalf,
                [text2]: centerTopHalf,
              },
              layout: {
                texts: 'bottom-top' as LayoutMetaData['texts'],
              },
            },
          ],
        ];
      case 3:
      default:
        return [[]];
    }
  }

  getTextAndImagesLayouts(scene: StudioScene): StudioScene[] {
    const { texts, images } = scene;

    const [text1, text2] = texts;
    const [image1, image2] = images;
    const textDescriptors = texts.map(id => this.currentScene.shapes[id]);
    const imageDescriptors = images.map(id => this.images[id]); // -- //
    const background: StudioShapeBackground = {
      type: 'color',
      color: '#848484',
      opacity: 0.6,
      radius: 32,
      stroke: {
        color: '#ffffff',
        width: 2,
      },
    };
    const [bg1, bg2] = ['bg-1', 'bg-2'];
    const layout: LayoutCellDefinition = {
      type: 'row',
      gap: 0.05,
      padding: [0.12, 0.15],
      children: [
        {
          type: 'row',
          gap: 0.05,
          children: [
            {
              shape: bg1,
              padding: [0, 0.1],
              gap: 0.05,
              type: 'column',
              background,
              children: [
                {
                  h: 0.5,
                  shape: text1,
                },
                {
                  h: 0.5,
                  padding: [0, 0.01],
                  shape: image1,
                },
              ],
            },
            {
              type: 'column',
              shape: bg2,
              padding: [0, 0.1],
              gap: 0.05,
              background,
              children: [
                {
                  h: 0.5,
                  shape: text2,
                },
                {
                  h: 0.5,
                  padding: [0, 0.03],
                  shape: image2,
                },
              ],
            },
          ],
        },
      ],
    };

    const shape = this.studioService.currentSceneShape;
    const rootCell = new RootLayoutCell(
      scene,
      layout,
      shape ? { W: shape.screenWidth, H: shape.screenHeight } : undefined,
    );
    const cellsByShape = rootCell.shapeCells;

    const [fs1, fs2] = [text1, text2]
      .filter(v => !!v)
      .map(id => this.currentScene.shapes[id] as StudioTextShapeDescriptor)
      .map(({ text, fontFamily }) => {
        return StudioTextShape.getFontSizeByCell(
          this.cs,
          leftHalf,
          text,
          fontFamily || this.currentFontFamily,
        );
      });

    const fontSize = Math.min(fs1, fs2);

    const type = 'cards';
    let newScene: StudioScene;

    console.log('cellsByShape', cellsByShape);
    switch (type) {
      case 'cards':
        newScene = {
          ...scene,
          layoutDefinition: layout,
          elements: [bg1, bg2],
          shapes: {
            ...scene.shapes,
            [bg1]: {
              type: 'rectangle',
              fill: {
                color: '#000000',
                opacity: 0.1,
              },
              margin: 0.05,
              radius: 32,
              cell: cellsByShape[bg1],
            },
            [bg2]: {
              type: 'rectangle',
              fill: {
                color: '#000000',
                opacity: 0.1,
              },
              margin: 0.05,
              radius: 32,
              cell: cellsByShape[bg2],
            },
            [text1]: {
              ...textDescriptors[0],
              cell: {
                ...cellsByShape[text1],
                fontSize,
              },
            },
            [image1]: {
              ...imageDescriptors[0],
              cell: cellsByShape[image1],
            },
            [text2]: {
              ...textDescriptors[1],
              cell: {
                ...cellsByShape[text2],
                fontSize,
              },
            },
            [image2]: {
              ...imageDescriptors[1],
              cell: cellsByShape[image2],
            },
          },
        };
        break;
    }

    console.log('newScene', newScene);
    return [newScene];
  }

  getOnlyTextLayouts(scene: StudioScene): StudioScene[] {
    const { texts } = scene;

    // Single text: show left / center / right layout options
    if (texts.length === 1) {
      const textId = texts[0];
      const textDescriptor = scene.shapes[textId];
      const { layoutDefinition: _ld, ...baseScene } = scene as any;
      return (
        [
          { x: 0, y: 0, w: 0.5, h: 1, align: 'left' as const, padding: 0.05 },
          { x: 0, y: 0, w: 1, h: 1, align: 'center' as const, padding: 0.2 },
          {
            x: 0.5,
            y: 0,
            w: 0.5,
            h: 1,
            align: 'right' as const,
            padding: 0.05,
          },
        ] as StudioCell[]
      ).map(cell => ({
        ...baseScene,
        shapes: {
          ...scene.shapes,
          [textId]: { ...textDescriptor, cell },
        },
      })) as StudioScene[];
    }

    console.log('texts', texts);

    const makeChildren = (): LayoutCellDefinition[] =>
      texts.map(id => ({ shape: id }));

    // 1. Column layout - texts stacked vertically
    const columnLayout: LayoutCellDefinition = {
      type: 'column',
      padding: [0.1, 0.15],
      gap: 0.03,
      children: makeChildren(),
    };

    // 2. Diagonal layout - top-left, center, bottom-right
    const diagonalLayout: LayoutCellDefinition = {
      type: 'column',
      padding: 0,
      gap: 0,
      children: makeChildren().map((child, i) => {
        const total = texts.length;
        const align = i === 0 ? 'left' : i === total - 1 ? 'right' : 'center';
        return {
          ...child,
          align,
          padding: [0.02, 0.1],
        } as LayoutCellDefinition;
      }),
    };

    // 3. Rows layout - texts arranged horizontally
    const rowLayout: LayoutCellDefinition = {
      type: 'row',
      padding: [0.15, 0.1],
      gap: 0.03,
      children: makeChildren(),
    };

    return [columnLayout, diagonalLayout, rowLayout].map(layoutDefinition => ({
      ...scene,
      layoutDefinition,
    })) as StudioScene[];
  }

  getOneImageAndTextsLayouts(scene: StudioScene): StudioScene[] {
    const { texts, images } = scene;
    const [imageId] = images;

    const textColumn: LayoutCellDefinition = {
      type: 'column',
      gap: 0.03,
      children: texts.map(id => ({ shape: id })),
    };

    const layouts: LayoutCellDefinition[] = [
      // Image left, texts right
      {
        type: 'row',
        padding: [0.05, 0.1],
        gap: 0.05,
        children: [{ shape: imageId }, textColumn],
      },
      // Texts left, image right
      {
        type: 'row',
        padding: [0.05, 0.1],
        gap: 0.05,
        children: [textColumn, { shape: imageId }],
      },
      // Image top, texts bottom
      {
        type: 'column',
        padding: [0.2, 0.2],
        gap: 0.05,
        children: [{ shape: imageId }, textColumn],
      },
    ];

    return layouts.map(layout => {
      const rootCell = new RootLayoutCell(scene, layout);
      const cellsByShape = rootCell.shapeCells;

      const updatedShapes: StudioScene['shapes'] = { ...scene.shapes };
      Object.keys(updatedShapes).forEach(id => {
        if (cellsByShape[id]) {
          updatedShapes[id] = { ...updatedShapes[id], cell: cellsByShape[id] };
        }
      });

      return { ...scene, layoutDefinition: layout, shapes: updatedShapes };
    });
  }

  getPossibleScenes(): StudioScene[] {
    // -- // -- //
    const { texts, images, videoInstances } = this.currentScene;

    const textLength = texts?.length || 0;
    const imageLength = images?.length || 0;
    const videoLength = videoInstances?.length || 0;
    const mediaLength = imageLength + videoLength;

    const onlyText = textLength && !mediaLength;
    const onlyImages = !textLength && imageLength && !videoLength;
    const onlyVideos = !textLength && !imageLength && videoLength;

    if (onlyText) {
      return this.getOnlyTextLayouts(this.currentScene);
    }

    if (textLength && imageLength === 1) {
      return this.getOneImageAndTextsLayouts(this.currentScene);
    }

    if (textLength && imageLength) {
      return this.getTextAndImagesLayouts(this.currentScene);
    }

    if (onlyImages) {
      console.log('onlyImages');
      return this.getImageLayout(images);
    }

    return this.getTextAndImagesLayouts(this.currentScene);
  }

  getCurrentSceneWithCellUpdate(id: string, cell: StudioCell): StudioScene {
    return {
      ...this.currentScene,
      shapes: {
        ...this.currentScene.shapes,
        [id]: {
          ...this.currentScene.shapes[id],
          cell,
        },
      },
    };
  }

  getImageLayout(images: string[]): StudioScene[] {
    let image1, image2, image3;
    let layouts;

    switch (images.length) {
      case 1:
        [image1] = images;
        layouts = [fullWidth, center07, center06, center05, center04, center03];

        return layouts.map(cell =>
          this.getCurrentSceneWithCellUpdate(image1, cell),
        ) as StudioScene[];
      case 2:
        // -- // -- //

        const children: LayoutCellDefinition[] = images.map(id => ({
          shape: id,
        }));

        layouts = [
          {
            type: 'row',
            padding: 0.2,
            gap: 0.01,
            children,
          },
          {
            type: 'row',
            padding: 0.3,
            gap: 0.05,
            children,
          },
          {
            type: 'column',
            padding: 0.4,
            gap: 0.01,
            children,
          },
          {
            type: 'column',
            padding: 0.4,
            gap: 0.05,
            children,
          },
        ];

        return layouts.map(layout => {
          return {
            ...this.currentScene,
            layoutDefinition: layout,
          };
        }) as StudioScene[];
      // -- // -- //
    }

    return [];
  }

  getPossibleLayouts(): {
    cells: Record<string, StudioCell>;
    layout: LayoutMetaData;
  }[][] {
    if (!this.currentScene) {
      return [];
    }

    const { texts, images, videoInstances } = this.currentScene;

    // -- //
    const noText = !texts?.length;
    const noImages = !images?.length;
    const noVideos = !videoInstances?.length;

    const onlyText = !noText && noImages && noVideos;

    if (onlyText) {
      return this.getTextLayouts(texts);
    }
    const onlyImages = !noImages && noText && noVideos;

    // if (onlyImages) {
    //   return this.getImageLayout(images);
    // }

    if (!texts?.length && !images?.length && !videoInstances?.length) {
      return [];
    }

    console.log('getPossibleLayouts', this.currentScene.layout);
    const currentLayout = this.currentScene.layout;
    if (!currentLayout) {
      // -- // -- //

      // Go for another approach

      const layouts = this.getTextBreakOptions(
        this.currentScene.shapes[texts[0]] as StudioTextShapeDescriptor,
      );

      // -- //

      return [];
    }

    const layoutOptions = this.getLayoutOptions(currentLayout);
    // Flatten the 2D array and convert each layout option to a shape ID mapping
    // const { texts, images, videoInstances } = this.currentScene;
    const mediaIds = [...images, ...videoInstances];

    const result: {
      cells: Record<string, StudioCell>;
      layout: LayoutMetaData;
    }[] = [];

    for (const mediaOptions of layoutOptions) {
      for (const layoutOption of mediaOptions) {
        const cells: Record<string, StudioCell> = {};

        // Map media cells
        const mediaCell = this.optionMap.media[layoutOption.media];
        if (mediaCell) {
          mediaIds.forEach(mediaId => {
            cells[mediaId] = mediaCell;
          });
        }

        // Map text cells
        const textCells = this.optionMap.texts[layoutOption.texts];
        if (textCells) {
          texts.forEach((textId, index) => {
            if (textCells[index]) {
              cells[textId] = textCells[index];
            }
          });
        }

        result.push({
          cells,
          layout: layoutOption as LayoutMetaData,
        });
      }
    }

    return [result];
  }

  twoTextsOneImage() {
    const {
      'text:0': textCell1,
      'text:1': textCell2,
      'image:0': imageCell,
    } = this.currentScene.cells;

    return [
      {
        name: 'left-right',
        cells: {
          'text:0': {
            ...textCell1,
            ...leftTopQuarter,
          },
          'text:1': {
            ...textCell2,
            ...leftBottomQuarter,
          },
          'image:0': {
            ...imageCell,
            ...rightHalf,
          },
        },
      },
      {
        name: 'right-left',
        cells: {
          'text:0': {
            ...textCell1,
            ...rightTopQuarter,
          },
          'text:1': {
            ...textCell2,
            ...rightBottomQuarter,
          },
          'image:0': {
            ...imageCell,
            ...leftHalf,
          },
        },
      },
      {
        name: 'top-bottom',
        cells: {
          'image:0': {
            ...imageCell,
            ...fullWidth,
          },
          'text:0': {
            ...textCell1,
            ...centerTopThird,
          },
          'text:1': {
            ...textCell2,
            ...centerBottomThird,
          },
        },
      },
    ];
  }

  updateLayoutRandom() {}

  /**
   * Returns the appropriate cell layout for a new media item (image or video)
   * based on the current shapes in the scene.
   *
   * Rules from layout.md:
   * 1. First media item: Place in `rightHalf` by default
   * 2. Second media item: Check existing placement, use opposite half
   * 3. Custom positioned items (`align: 'custom'`): Skip during auto-layout
   */
  getNewMediaLayout(shapes: Record<string, { cell: StudioCell }>): StudioCell {
    // Filter out custom-aligned shapes (user-positioned elements)
    const nonCustomShapes = Object.values(shapes).filter(
      shape => shape?.cell?.align !== undefined,
    );

    const mediaCount = nonCustomShapes.length;

    switch (mediaCount) {
      case 0:
        // First media item: rightHalf by default
        return rightHalf;

      case 1:
        // Second media item: use opposite half
        const existingCell = nonCustomShapes[0].cell;
        if (isLeftHalf(existingCell)) {
          return rightHalf;
        } else if (isRightHalf(existingCell)) {
          return leftHalf;
        }
        // If existing is not a half, default to leftHalf
        return leftHalf;

      case 2:
        // Third media item: use quarters
        // Check which halves are occupied
        const hasLeft = nonCustomShapes.some(s => isLeftHalf(s.cell));
        const hasRight = nonCustomShapes.some(s => isRightHalf(s.cell));

        if (hasLeft && hasRight) {
          // Both halves occupied, use top-right quarter
          return rightTopQuarter;
        } else if (hasLeft) {
          return rightHalf;
        } else if (hasRight) {
          return leftHalf;
        }
        return rightTopQuarter;

      case 3:
        // Fourth media item: remaining quarter
        return rightBottomQuarter;

      default:
        // More than 4 items: use thirds
        const thirdIndex = mediaCount % 4;
        const thirds = [
          leftTopThird,
          rightTopThird,
          leftBottomThird,
          rightBottomThird,
        ];
        return thirds[thirdIndex];
    }
  }

  updateLayoutByRemove(cellId: string) {
    const { texts: currentTexts, images, videoInstances } = this.currentScene;
    const textLength = currentTexts?.length || 0;
    const imageLength = images?.length || 0;
    const videoLength = videoInstances?.length || 0;

    if (textLength + imageLength + videoLength == 2) {
      const cells = omit(this.currentScene.cells, cellId);
      // -- // -- //
    }
  }
  updateLayoutByAdd({
    text,
    image,
    video,
  }: {
    text?: string;
    image?: string;
    video?: string;
  }) {
    // -- //
    const { texts: currentTexts, images, videoInstances } = this.currentScene;
    // -- // -- // -- //

    const textLength = currentTexts?.length || 0;
    const imageLength = images?.length || 0;
    const videoLength = videoInstances?.length || 0;

    //

    if (text) {
      // -- //
    }

    if (image) {
      // -- //
    }

    if (video) {
      // -- //
    }
  }

  saveCurrentScene() {
    this.store.dispatch(
      saveScene({
        value: cloneDeep(this.currentScene),
      }),
    );
  }

  addText(text: string) {}

  updateText(index: number, text: string) {
    const id = this.currentScene.texts[index];
    if (!id) {
      return;
    }
    (this.currentScene.shapes[id] as StudioTextShapeDescriptor).text = text;
    this.saveCurrentScene();
  }
  removeText(index: number) {
    const id = this.currentScene.texts[index];
    this.removeShape(this.currentScene.shapes[id], id);
    this.currentScene.texts.splice(index, 1);
    this.saveCurrentScene();
  }

  addImages(images: StudioImage[]) {
    images.forEach(image => {
      this.addImage(image);
    });
  }
  addImage(image: StudioImage) {
    this.images[image.id] = image;
    const newImageShape: StudioImageShapeDescriptor = {
      imageId: image.id,
      cell: null,
    };
    // -- //
    this.addNewShape(newImageShape);
    this.saveCurrentScene();
  }

  removeImage(index: number) {
    const id = this.currentScene.images[index];
    if (!this.currentScene.shapes[id]) {
      console.warn('No image to remove in the scene descriptor');
      return;
    }
    this.removeShape(this.currentScene.shapes[id], id);
    this.currentScene.images.splice(index, 1);
    this.saveCurrentScene();
  }

  addVideos(videos: VideoInstance[]) {
    // videos.forEach(video => {
    //   this.addVideo(video);
    // });
  }
  addVideo(videoShape: StudioVideoShapeDescriptor) {
    console.log('addVideo', videoShape);
    this.currentScene.videoInstances.push(videoShape.id);
    this.currentScene.shapes[videoShape.id] = videoShape;
    this.saveCurrentScene();
  }

  removeVideo(index: number) {
    const id = this.currentScene.videoInstances[index];
    this.removeShape(this.currentScene.shapes[id], id);
    this.currentScene.videoInstances.splice(index, 1);
    this.saveCurrentScene();
  }

  removeShape(shape: StudioShapeDescriptor, id: string) {
    delete this.currentScene.shapes[id];
    const { shapes } = this.currentScene;

    if (Object.values(shapes).length == 0) {
      return;
    }

    const { x, y, w, h } = shape.cell;

    if (w == 1 && h == 1) {
      return;
    }

    const [xStart, xEnd] = [x, x + w];
    const [yStart, yEnd] = [y, y + h];

    const between = (a: number, a1: number, a2: number) => a1 <= a && a <= a2;

    const shapesInSameRow = Object.values(shapes).filter(({ cell }) => {
      const [cellYStart, cellYEnd] = [cell.y, cell.y + cell.h];
      return (
        between(cellYStart, yStart, yEnd) && between(cellYEnd, yStart, yEnd)
      );
    });

    const shapesInSameColumn = Object.values(shapes).filter(({ cell }) => {
      const [cellXStart, cellXEnd] = [cell.x, cell.x + cell.w];
      return (
        between(cellXStart, xStart, xEnd) && between(cellXEnd, xStart, xEnd)
      );
    });

    if (shapesInSameRow.length == 0 && shapesInSameColumn.length == 0) {
      // console.warn('This should not happen!');
      return;
    }

    if (shapesInSameRow.length > shapesInSameColumn.length) {
      this.removeHorizontal(w, xStart);
    } else {
      this.removeVertical(h, yStart);
    }
    // if (shapesInSameRow.length > 1) {
    //   this.removeHorizontal(w);
    // } else if (shapesInSameColumn.length > 1) {
    //   this.removeVertical(h);
    // }
  }

  removeVertical(height: number, yStart: number) {
    const ratio = 1 / (1 - height);
    // -- //
    for (const id in this.currentScene.shapes) {
      if (this.currentScene.shapes[id].cell.y > yStart) {
        this.currentScene.shapes[id].cell.y -= height;
      }
      this.currentScene.shapes[id].cell.y *= ratio;
      this.currentScene.shapes[id].cell.h *= ratio;
    }
  }

  removeHorizontal(width: number, xStart: number) {
    const ratio = 1 / (1 - width);
    // -- //
    for (const id in this.currentScene.shapes) {
      if (this.currentScene.shapes[id].cell.x > xStart) {
        this.currentScene.shapes[id].cell.x -= width;
      }
      this.currentScene.shapes[id].cell.x *= ratio;
      this.currentScene.shapes[id].cell.w *= ratio;
    }
    // -- //
  }

  removeVideoByInstanceId(videoInstanceId: string) {
    const index = this.currentScene.videoInstances.indexOf(videoInstanceId);
    if (index === -1) {
      return;
    }
    this.removeVideo(index);
  }

  // removeVideo(index: number) {
  //   if (!this.currentScene?.shapes) {
  //     return;
  //   }

  //   const videoId = this.currentScene.videoInstances[index];
  //   if (!this.currentScene.shapes[videoId]) {
  //     console.warn('Video shape not found for index:', index);
  //     // return;
  //   }

  //   // Remove the shape
  //   delete this.currentScene.shapes[videoId];
  //   this.currentScene.videoInstances.splice(index, 1);
  //   // -- //
  //   this.saveCurrentScene();
  // }

  imagesChanged(selectedImages: string[]) {
    // -- //
    this.currentScene.images ||= [];
    const newImages = selectedImages.filter(
      image => !this.currentScene.images.includes(image),
    );

    const deletedImages = this.currentScene.images.filter(
      image => !selectedImages.includes(image),
    );

    deletedImages.forEach(image => {
      delete this.currentScene.shapes[image];
      this.removeShape(this.currentScene.shapes[image], image); // -- //
    });

    if (newImages.length) {
      newImages.forEach(image => {
        this.addNewShape({ imageId: image, cell: undefined });
      });
    }
    this.currentScene.images = selectedImages;

    this.saveCurrentScene();
  }

  setSceneCells(studioScene: StudioScene, layout: LayoutCellDefinition) {
    // -- //
    const layoutCells = new RootLayoutCell(studioScene, layout).shapeCells;

    Object.entries(studioScene.shapes).forEach(([id, shape]) => {
      shape.cell = layoutCells[id];
    });

    return studioScene;
  }
}
